/**
 * Tile-space flood fill with symmetry wrap — bucket tool.
 *
 * Operates on the 512² layer raster. The frontier never paints outside the
 * tile polygon (those margins are clipped at render time); instead, when it
 * reaches a pixel whose centre is outside, the pixel is mapped through the
 * variant's wrap (symmetry transform) back inside and the fill continues
 * there — so a region spanning a tile edge fills completely, just like a
 * stroke crossing the border.
 */

export interface FloodFillOptions {
  /** RGBA pixels, length size*size*4 */
  data: Uint8ClampedArray;
  size: number;
  /** 1 = pixel centre inside the tile polygon */
  inside: Uint8Array;
  /** map an outside pixel to its wrapped inside pixel, or null if unmappable */
  wrap: (px: number, py: number) => [number, number] | null;
  seedX: number;
  seedY: number;
  /** max per-channel colour difference to still count as the seed colour */
  tolerance: number;
}

/**
 * Returns a mask (1 = fill this pixel) covering the connected region of the
 * seed colour, or null if the seed is out of range.
 */
export function floodFillTile(opts: FloodFillOptions): Uint8Array | null {
  const { data, size, inside, wrap, tolerance } = opts;
  let { seedX, seedY } = opts;
  const idx = (x: number, y: number): number => y * size + x;
  const inRange = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < size && y < size;

  if (!inRange(seedX, seedY)) return null;
  if (inside[idx(seedX, seedY)] !== 1) {
    const wrapped = wrap(seedX, seedY);
    if (!wrapped || !inRange(wrapped[0], wrapped[1]) || inside[idx(wrapped[0], wrapped[1])] !== 1) {
      return null;
    }
    [seedX, seedY] = wrapped;
  }

  // Sample the seed colour. Fully transparent pixels are all "background"
  // regardless of their stale RGB values.
  const si = idx(seedX, seedY) * 4;
  const seedA = data[si + 3]!;
  const seedR = seedA === 0 ? 0 : data[si]!;
  const seedG = seedA === 0 ? 0 : data[si + 1]!;
  const seedB = seedA === 0 ? 0 : data[si + 2]!;

  const matches = (x: number, y: number): boolean => {
    const i = idx(x, y) * 4;
    const a = data[i + 3]!;
    const r = a === 0 ? 0 : data[i]!;
    const g = a === 0 ? 0 : data[i + 1]!;
    const b = a === 0 ? 0 : data[i + 2]!;
    return (
      Math.abs(r - seedR) <= tolerance &&
      Math.abs(g - seedG) <= tolerance &&
      Math.abs(b - seedB) <= tolerance &&
      Math.abs(a - seedA) <= tolerance
    );
  };

  const mask = new Uint8Array(size * size);
  const visited = new Uint8Array(size * size);
  const stack: number[] = [idx(seedX, seedY)];
  visited[idx(seedX, seedY)] = 1;

  while (stack.length > 0) {
    const cur = stack.pop()!;
    const cx = cur % size;
    const cy = (cur - cx) / size;
    mask[cur] = 1;

    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!inRange(nx, ny)) continue;
      const ni = idx(nx, ny);
      if (visited[ni]) continue;
      visited[ni] = 1;

      if (inside[ni] === 1) {
        if (matches(nx, ny)) stack.push(ni);
        continue;
      }

      // Stepped off the tile — continue at the wrapped position.
      const wrapped = wrap(nx, ny);
      if (!wrapped) continue;
      const [mx, my] = wrapped;
      if (!inRange(mx, my)) continue;
      const mi = idx(mx, my);
      if (visited[mi] || inside[mi] !== 1) continue;
      visited[mi] = 1;
      if (matches(mx, my)) stack.push(mi);
    }
  }

  return mask;
}
